import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, List, ListItemButton } from '@mui/material';
import './profileSections.css';
import ManageAccountProfile from './ManageAccountProfile.js';
import ManageOrganization from './ManageOrganization.js';

import AlertDialog from '../../Dialog.js';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link } from 'react-router-dom';
import AccountCircle from '@mui/icons-material/AccountCircle';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import LanguageIcon from '@mui/icons-material/Language';
import WidgetsIcon from '@mui/icons-material/Widgets';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import UserList from '../UserManagement/UserList.js';
import { useDispatch } from 'react-redux';
import { resetGlobalState, persistor } from '../../store.js';
import { useNavigate, useLocation } from 'react-router-dom';
import MiniDrawer from '../../components/MiniDrawer.js';
import { supabase } from '../../supabaseClient.js';
import TemplateList from './Template/TemplateList.js';
import PrivacySettings from './PrivacySettings.js';
import LanguageSettings from './LanguageSettings.js';
import Help from './Help.js';
import SubscriptionView from './Subscription/SubscriptionView.js';

import CorporateFareIcon from '@mui/icons-material/CorporateFare';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const SettingsPage = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(() => new URLSearchParams(location.search).has('tour'));
  const mobileMenuButton = useRef(null);
  useEffect(() => {
    const revealNavigation = event => {
      const target = event.detail?.target;
      if (target && document.querySelector(target)?.closest('#settings-menu-content')) setMobileMenuOpen(true);
    };
    window.addEventListener('profile-reveal-target', revealNavigation);
    return () => window.removeEventListener('profile-reveal-target', revealNavigation);
  }, []);
  const bankManagementRequested = new URLSearchParams(location.search).get('section') === 'organization';
  const bankOrgId = new URLSearchParams(location.search).get('bankOrg');
  const settingsTour = new URLSearchParams(location.search).get('settingsTour');
  // Capture the flag once, then clear it from history state below so a page
  // refresh or back-navigation doesn't keep forcing the Update Profile view.
  const profileGuideRequested = new URLSearchParams(location.search).get('guide') === 'profile';

  const [openUpdateProfile] = useState(() => Boolean(location.state?.openUpdateProfile));
  const [selectedOption, setSelectedOption] = useState(bankManagementRequested ? 'Manage Organization' : openUpdateProfile || profileGuideRequested ? 'Manage Account and Profile' : 'Subscription');

  const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.openUpdateProfile) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settingsTour === 'profile' || settingsTour === 'profile-edit') setSelectedOption('Manage Account and Profile');
    if (profileGuideRequested) setSelectedOption('Manage Account and Profile');
    if (settingsTour === 'subscription' || settingsTour === 'subscription-usage' || settingsTour === 'subscription-connections') setSelectedOption('Subscription');
    if (settingsTour === 'templates') setSelectedOption('Template Builder');
    if (bankManagementRequested || settingsTour === 'organization' || settingsTour === 'location') setSelectedOption('Manage Organization');
  }, [settingsTour, profileGuideRequested, bankManagementRequested, bankOrgId, location.key]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error logging out: ', error.message);
    }
    dispatch(resetGlobalState());
    await persistor.purge();
    navigate('/');
  };

  const items = [
    ['Subscription', 'Plans, Billing and Subscription', CreditCardIcon, 'settings-subscription'],
    ['Manage Account and Profile', 'Manage Account and Profile', AccountCircle, 'settings-profile'],
    ['Manage Organization', 'Manage Organization', CorporateFareIcon, 'settings-organization'],
    ['Manage Users', 'Manage Users', PeopleIcon],
    ['Privacy Settings', 'Privacy Settings', PrivacyTipIcon],
    ['Language Settings', 'Language Settings', LanguageIcon],
    ['Template Builder', 'Template Builder', WidgetsIcon, 'settings-templates'],
    ['Help', 'Help and Support', HelpIcon],
  ];
  const selectedLabel = items.find(([value]) => value === selectedOption)?.[1] || 'Settings';
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f7f9f9', width: '100%', minWidth: 0 }}>
      <Box sx={{ flexShrink: 0 }}><MiniDrawer /></Box>
      <Box className="settings-shell">
        <Box component="nav" aria-label="Settings" className="settings-navigation">
          <Box className="settings-mobile-toolbar">
            <Button component={Link} to="/consultation" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>Workspace</Button>
            <Button ref={mobileMenuButton} aria-label="Settings menu" aria-expanded={mobileMenuOpen} aria-controls="settings-menu-content" onClick={() => setMobileMenuOpen(value => !value)} endIcon={<ExpandMoreIcon sx={{ transform: mobileMenuOpen ? 'rotate(180deg)' : undefined }} />} sx={{ textTransform: 'none' }}>{selectedLabel}</Button>
          </Box>
          <Box id="settings-menu-content" className={`settings-menu-content${mobileMenuOpen ? ' open' : ''}`}>
          <Button component={Link} to="/consultation" startIcon={<ArrowBackIcon />} sx={{ mb: 4, px: 2, py: 1.5, textTransform: 'none', color: 'text.primary', '& .MuiButton-startIcon': { color: '#58bdbb', mr: 2.5 } }}>Back to Workspace</Button>
          <List disablePadding>{items.map(([value, label, Icon, tour]) => <ListItemButton key={value} data-tour={tour} selected={selectedOption === value} onClick={() => { setSelectedOption(value); if (mobileMenuOpen) mobileMenuButton.current?.focus(); setMobileMenuOpen(false); }} sx={{ borderRadius: '10px', my: 1, py: 1.5, px: 2, color: 'text.primary', '&.Mui-selected': { bgcolor: '#fff' }, '&.Mui-selected:hover': { bgcolor: '#fff' } }}>
            <Icon sx={{ mr: 2.5, color: '#58bdbb' }} /><Typography variant="body1">{label}</Typography>
          </ListItemButton>)}</List>
          <Button startIcon={<LogoutIcon />} onClick={() => setOpenDialog({ open: true, content: 'Are you sure you want to logout of your account?', confirmAction: handleLogout })} sx={{ textTransform: 'none', color: 'text.primary', px: 2, py: 1.5, '& .MuiButton-startIcon': { color: '#58bdbb', mr: 2.5 } }}>Logout</Button>
          </Box>
        </Box>
        <Box component="main" className="settings-content">
          {selectedOption === 'Manage Account and Profile' ? <ManageAccountProfile initialOption="Update profile" showWelcome={openUpdateProfile} />
            : selectedOption === 'Subscription' ? <SubscriptionView tourTab={settingsTour === 'subscription-usage' ? 'usage' : settingsTour === 'subscription-connections' ? 'connections' : 'details'} />
            : selectedOption === 'Manage Organization' ? <ManageOrganization initialBankOrgId={bankOrgId} />
            : selectedOption === 'Manage Users' ? <UserList embedded />
            : selectedOption === 'Privacy Settings' ? <PrivacySettings />
            : selectedOption === 'Language Settings' ? <LanguageSettings />
            : selectedOption === 'Template Builder' ? <TemplateList />
            : selectedOption === 'Help' ? <Help /> : null}
        </Box>
      </Box>
      <AlertDialog data={{ open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog }} />
    </Box>
  );
};
export default SettingsPage;
